import React, { useState, useEffect } from "react";
import { User, Save } from "lucide-react";
import { getImageUrl } from "@/utils/imageUrl";
import avatarDefault from "@/assets/images/profile/avatar_photo.png";
import backgroundDefault from "@/assets/images/profile/background_photo.jpg";
import DatePicker from "../../common/DatePicker";
import SelectDropdown from "../../common/SelectDropdown";
import TextInput from "../../common/TextInput";
import TextArea from "../../common/TextArea";
import SettingsSection from "./SettingsSection";
import CityAutocomplete from "../../common/CityAutocomplete";

const ProfileInfo = ({
  formData,
  setFormData,
  user,
  avatarPreview,
  coverPreview,
  newAvatarFile,
  newCoverFile,
  handleAvatarUpload,
  handleCoverUpload,
  handleDeleteAvatar,
  handleDeleteCover,
  reviewChangesBeforeSave,
}) => {
  const BIO_MAX = 100;

  const getBioCounterColor = () => {
    const len = formData.bio.length;
    if (len > BIO_MAX * 0.95) return "text-red-500";
    if (len > BIO_MAX * 0.8) return "text-yellow-500";
    return "text-gray-500 dark:text-gray-400";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <SettingsSection title="Profile Information" icon={<User />}>
      {/* ---------- Avatar & Cover Upload Section ---------- */}
      <div className="w-full flex flex-col gap-8 mb-8">
        {/* Cover Photo Preview + Upload */}
        <div className="w-full relative h-48 rounded-3xl overflow-hidden bg-gray-100 dark:bg-gray-700/50 shadow-sm border border-gray-100 dark:border-gray-700 group">
          <img
            src={
              coverPreview ||
              (user?.cover_photo_url
                ? getImageUrl(user.cover_photo_url)
                : null) ||
              backgroundDefault
            }
            alt="Cover"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />

          {/* Action Buttons */}
          <div className="absolute bottom-4 right-4 flex items-center gap-3">
            {/* Delete Cover (if exists) */}
            {user?.cover_photo_url && !newCoverFile && (
              <button
                type="button"
                onClick={handleDeleteCover}
                className="bg-red-500/90 text-white px-4 py-2 rounded-full text-xs font-semibold backdrop-blur-sm hover:bg-red-600 transition-colors shadow-lg"
              >
                Remove
              </button>
            )}

            <label className="bg-white/90 dark:bg-gray-800/90 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-full cursor-pointer shadow-lg text-xs font-semibold backdrop-blur-md hover:bg-white dark:hover:bg-gray-800 transition-all hover:scale-105 active:scale-95">
              Change Cover
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverUpload}
              />
            </label>
          </div>
        </div>

        {/* Avatar Preview + Upload */}
        <div className="flex items-center gap-6 px-2">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full p-1 bg-white dark:bg-gray-800 shadow-xl ring-1 ring-gray-100 dark:ring-gray-700">
              <img
                src={
                  avatarPreview ||
                  (user?.avatar_url ? getImageUrl(user.avatar_url) : null) ||
                  avatarDefault
                }
                alt="Avatar"
                className="w-full h-full rounded-full object-cover"
              />
            </div>

            {/* Quick Edit Overlay (visible on hover) */}
            <label className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity duration-200">
              <span className="text-xs font-semibold">Change</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
              />
            </label>
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">
              Profile Picture
            </h3>
            <div className="flex items-center gap-3">
              <label className="text-sm text-green-600 dark:text-green-400 font-medium cursor-pointer hover:underline">
                Upload New
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
              </label>

              {user?.avatar_url && !newAvatarFile && (
                <>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={handleDeleteAvatar}
                    className="text-sm text-red-500 font-medium hover:underline"
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
            <p className="text-xs text-gray-400">
              Recommended 400x400px. JPG, PNG supported.
            </p>
          </div>
        </div>
      </div>
      <form className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Username */}
        <TextInput
          label="Username"
          id="username"
          value={formData.username}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              username: e.target.value.toLowerCase(),
            }))
          }
          placeholder="Enter your username"
        />
        <p className="-mt-2 text-xs text-gray-500 sm:col-span-2">
          Only lowercase letters, numbers, underscores (3–30 chars)
        </p>

        {/* Name */}
        <TextInput
          label="Full Name"
          id="name"
          value={formData.name}
          onChange={handleInputChange} // handleInputChange expects e.target.name/value. TextInput passes event so this works.
          name="name"
          placeholder="Enter your full name"
        />

        {/* Location */}
        <div className="sm:col-span-1">
          <CityAutocomplete
            label="Location"
            name="location"
            value={formData.location}
            onChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
            placeholder="Enter your location (City, State)"
          />
        </div>

        {/* Date of Birth */}
        <div className="sm:col-span-1">
          <DatePicker
            label="Date of Birth"
            value={formData.date_of_birth}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                date_of_birth: e.target.value,
              }))
            }
            showAge={true}
            allowFuture={false}
          />
        </div>

        {/* Gender */}
        <div className="sm:col-span-1">
          <SelectDropdown
            label="Gender"
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
              { value: "other", label: "Other" },
              { value: "prefer_not_to_say", label: "Prefer not to say" },
            ]}
            value={formData.gender}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, gender: e.target.value }))
            }
            placeholder="Select Gender"
          />
        </div>

        {/* Bio */}
        <div className="sm:col-span-2">
          <TextArea
            label="Bio"
            name="bio"
            rows={3}
            maxLength={BIO_MAX}
            value={formData.bio}
            onChange={handleInputChange}
            placeholder="Let others know more about you..."
          />
          {/* Re-adding simple counter below if TextArea doesn't have it. */}
          <div className={`text-xs text-right mt-1 ${getBioCounterColor()}`}>
            {formData.bio.length}/{BIO_MAX}
          </div>
        </div>
      </form>
      <button
        onClick={reviewChangesBeforeSave}
        className="mt-5 px-5 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition btn-anim"
      >
        <Save className="inline w-4 h-4 mr-2" />
        Save Changes
      </button>
    </SettingsSection>
  );
};

export default ProfileInfo;
